import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'secuguard_auto_refresh';
const INTERVAL_MS = 60000;

interface AutoRefreshContextType {
  enabled: boolean;
  toggle: () => void;
  secondsLeft: number;
  subscribe: (callback: () => void) => () => void;
}

const AutoRefreshContext = createContext<AutoRefreshContextType>({
  enabled: false,
  toggle: () => {},
  secondsLeft: 0,
  subscribe: () => () => {},
});

export function AutoRefreshProvider({ children }: { children: React.ReactNode }) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
  const [secondsLeft, setSecondsLeft] = useState(INTERVAL_MS / 1000);
  const subscribersRef = useRef<Set<() => void>>(new Set());
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback);
    return () => { subscribersRef.current.delete(callback); };
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      if (next) setSecondsLeft(INTERVAL_MS / 1000);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (countdownRef.current) clearInterval(countdownRef.current);
      return;
    }

    setSecondsLeft(INTERVAL_MS / 1000);

    countdownRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          subscribersRef.current.forEach((cb) => cb());
          return INTERVAL_MS / 1000;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [enabled]);

  return (
    <AutoRefreshContext.Provider value={{ enabled, toggle, secondsLeft, subscribe }}>
      {children}
    </AutoRefreshContext.Provider>
  );
}

export const useAutoRefresh = () => useContext(AutoRefreshContext);
